import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../api/axios';
import { useAlert } from '../context/AlertContext';
import { useSocket } from '../context/SocketContext';
import './Settings.css';

function Settings() {
  const { platform } = useParams();
  const [settings, setSettings] = useState({});
  const [youtubeOAuthConnected, setYoutubeOAuthConnected] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const [paypalTestResult, setPaypalTestResult] = useState(null);
  const alert = useAlert();
  const { socket } = useSocket();

  useEffect(() => {
    loadSettings();
  }, [platform]);

  useEffect(() => {
    if (!socket || platform !== 'streamelements') return;

    const onProgress = (data) => setImportProgress(data);
    const onComplete = (data) => {
      setImportProgress(null);
      setImportStatus(`Letzter Import: ${data.imported} importiert, ${data.updated} aktualisiert, ${data.errors} Fehler`);
    };
    const onError = (data) => {
      setImportProgress(null);
      setImportStatus(`Fehler: ${data.message}`);
    };

    socket.on('import:progress', onProgress);
    socket.on('import:complete', onComplete);
    socket.on('import:error', onError);

    return () => {
      socket.off('import:progress', onProgress);
      socket.off('import:complete', onComplete);
      socket.off('import:error', onError);
    };
  }, [socket, platform]);

  const loadSettings = async () => {
    try {
      if (platform === 'paypal') {
        const { data } = await axios.get('/api/paypal/settings');
        setSettings(data);
        return;
      }
      const { data } = await axios.get(`/api/settings/${platform}`);
      setSettings(data);
      if (platform === 'youtube') {
        setYoutubeOAuthConnected(!!(data.oauthAccessToken));
      }
    } catch (error) {
      alert.error('Fehler beim Laden der Einstellungen');
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      if (platform === 'paypal') {
        await axios.post('/api/paypal/settings', settings);
      } else {
        await axios.post(`/api/settings/${platform}`, settings);
      }
      alert.success('Einstellungen erfolgreich gespeichert!');
    } catch (error) {
      alert.error('Fehler beim Speichern der Einstellungen');
    }
  };

  const updateSetting = (key, value) => {
    setSettings({ ...settings, [key]: value });
  };

  const loadLastImport = async () => {
    try {
      const { data } = await axios.get('/api/settings/streamelements');
      if (data.lastImport) setImportStatus(`Letzter Import: ${data.lastImport}`);
    } catch (_) {}
  };

  const startImport = async () => {
    try {
      setImportProgress({ processed: 0, total: 0, type: 'points' });
      await axios.post('/api/settings/streamelements/import');
      alert.success('Import gestartet.');
    } catch (error) {
      setImportProgress(null);
      alert.error('Fehler beim Starten des Imports: ' + (error.response?.data?.error || error.message));
    }
  };

  const startYouTubeOAuth = async () => {
    try {
      const { data } = await axios.get('/api/settings/youtube/oauth/url');
      window.open(data.url, '_blank', 'width=600,height=700');
      // Poll for connection status after a short delay
      setTimeout(() => loadSettings(), 3000);
    } catch (error) {
      alert.error('Fehler beim Starten des OAuth-Flows: ' + (error.response?.data?.error || error.message));
    }
  };

  const disconnectYouTubeOAuth = async () => {
    try {
      await axios.delete('/api/settings/youtube/oauth');
      setYoutubeOAuthConnected(false);
      alert.success('YouTube OAuth-Verbindung getrennt.');
      loadSettings();
    } catch (error) {
      alert.error('Fehler beim Trennen der OAuth-Verbindung');
    }
  };

  const platformTitles = {
    'twitch-main': 'Twitch Main Bot',
    'twitch-247': 'Twitch 24/7 Stream',
    'youtube': 'YouTube Chat Bot',
    'discord': 'Discord Bot',
    'streamelements': 'StreamElements',
    'paypal': 'PayPal Donations'
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <h2>{platformTitles[platform]}</h2>
        <p>Konfiguriere die Einstellungen für {platformTitles[platform]}</p>
      </div>

      <form onSubmit={saveSettings} className="settings-form">
        {platform.startsWith('twitch') && (
          <>
            <div className="form-group">
              <label>Bot Username</label>
              <input
                type="text"
                value={settings.username || ''}
                onChange={(e) => updateSetting('username', e.target.value)}
                placeholder="dein_bot_username"
              />
            </div>

            <div className="form-group">
              <label>OAuth Token</label>
              <input
                type="password"
                value={settings.oauth || ''}
                onChange={(e) => updateSetting('oauth', e.target.value)}
                placeholder="oauth:..."
              />
              <small>Generiere einen Token auf <a href="https://twitchapps.com/tmi/" target="_blank" rel="noopener noreferrer">twitchapps.com/tmi</a></small>
            </div>

            <div className="form-group">
              <label>Channel Name</label>
              <input
                type="text"
                value={settings.channel || ''}
                onChange={(e) => updateSetting('channel', e.target.value)}
                placeholder="channel_name"
              />
            </div>

            {platform === 'twitch-247' && (
              <>
                <div className="form-group">
                  <label>OBS WebSocket URL</label>
                  <input
                    type="text"
                    value={settings.obsUrl || 'ws://localhost:4456'}
                    onChange={(e) => updateSetting('obsUrl', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>OBS WebSocket Password</label>
                  <input
                    type="password"
                    value={settings.obsPassword || ''}
                    onChange={(e) => updateSetting('obsPassword', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Video Playlist Ordner</label>
                  <input
                    type="text"
                    value={settings.videoFolder || ''}
                    onChange={(e) => updateSetting('videoFolder', e.target.value)}
                    placeholder="C:/Videos/Stream"
                  />
                </div>
              </>
            )}

            {platform === 'twitch-main' && (
              <>
                <div className="form-group">
                  <label>OBS WebSocket URL</label>
                  <input
                    type="text"
                    value={settings.obsUrl || 'ws://localhost:4444'}
                    onChange={(e) => updateSetting('obsUrl', e.target.value)}
                  />
                  <small>Standard Port ist 4444</small>
                </div>

                <div className="form-group">
                  <label>OBS WebSocket Password</label>
                  <input
                    type="password"
                    value={settings.obsPassword || ''}
                    onChange={(e) => updateSetting('obsPassword', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Pause Video Ordner</label>
                  <input
                    type="text"
                    value={settings.pauseVideoFolder || ''}
                    onChange={(e) => updateSetting('pauseVideoFolder', e.target.value)}
                    placeholder="C:/Videos/Pause"
                  />
                  <small>Ordner mit Videos für die Pause Scene</small>
                </div>

                <div className="form-group">
                  <label>Twitch Client ID</label>
                  <input
                    type="text"
                    value={settings.clientId || ''}
                    onChange={(e) => updateSetting('clientId', e.target.value)}
                    placeholder="deine_twitch_app_client_id"
                  />
                  <small>Erstelle eine App auf <a href="https://dev.twitch.tv/console/apps" target="_blank" rel="noopener noreferrer">dev.twitch.tv</a> für Zuschauerzahlen</small>
                </div>

                <div className="form-group">
                  <label>Twitch Client Secret</label>
                  <input
                    type="password"
                    value={settings.clientSecret || ''}
                    onChange={(e) => updateSetting('clientSecret', e.target.value)}
                    placeholder="dein_client_secret"
                  />
                </div>

                <div className="form-group">
                  <label>Endscreen Scene Name</label>
                  <input
                    type="text"
                    value={settings.endscreenScene || 'Endscreen'}
                    onChange={(e) => updateSetting('endscreenScene', e.target.value)}
                    placeholder="Endscreen"
                  />
                  <small>OBS Scene die beim Raid angezeigt wird</small>
                </div>

                <div className="form-group">
                  <label>Raid Verzögerung (Sekunden)</label>
                  <input
                    type="number"
                    value={settings.raidDelay || 60}
                    onChange={(e) => updateSetting('raidDelay', parseInt(e.target.value))}
                    min="10"
                    max="300"
                  />
                  <small>Zeit zwischen Endscreen und Raid (Standard: 60 Sekunden)</small>
                </div>

                <div className="form-group">
                  <label>Musik Quelle (OBS)</label>
                  <input
                    type="text"
                    value={settings.musicSource || ''}
                    onChange={(e) => updateSetting('musicSource', e.target.value)}
                    placeholder="Musik"
                  />
                  <small>Name der OBS-Medienquelle für !music Command</small>
                </div>
              </>
            )}
          </>
        )}

        {platform === 'youtube' && (
          <>
            <div className="form-group">
              <label>YouTube API Keys</label>
              <small style={{ display: 'block', marginBottom: '10px' }}>
                Mehrere Keys für höhere Rate Limits. Das System rotiert automatisch zwischen den Keys.
              </small>
              {(settings.apiKeys || ['']).map((key, index) => (
                <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <input
                    type="password"
                    value={key}
                    onChange={(e) => {
                      const newKeys = [...(settings.apiKeys || [''])];
                      newKeys[index] = e.target.value;
                      updateSetting('apiKeys', newKeys);
                    }}
                    placeholder={`API Key ${index + 1}`}
                    style={{ flex: 1 }}
                  />
                  {index > 0 && (
                    <button
                      type="button"
                      className="btn-danger"
                      onClick={() => {
                        const newKeys = settings.apiKeys.filter((_, i) => i !== index);
                        updateSetting('apiKeys', newKeys);
                      }}
                      style={{ padding: '10px 15px' }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  const newKeys = [...(settings.apiKeys || ['']), ''];
                  updateSetting('apiKeys', newKeys);
                }}
                style={{ marginTop: '5px' }}
              >
                + API Key hinzufügen
              </button>
              <small style={{ display: 'block', marginTop: '10px' }}>
                Erstelle Keys in der <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer">Google Cloud Console</a>
              </small>
            </div>

            <div className="form-group">
              <label>Channel ID</label>
              <input
                type="text"
                value={settings.channelId || ''}
                onChange={(e) => updateSetting('channelId', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>OAuth2 Client ID</label>
              <input
                type="text"
                value={settings.oauthClientId || ''}
                onChange={(e) => updateSetting('oauthClientId', e.target.value)}
                placeholder="Google OAuth2 Client ID"
              />
              <small>Erstelle OAuth2-Credentials in der <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer">Google Cloud Console</a></small>
            </div>

            <div className="form-group">
              <label>OAuth2 Client Secret</label>
              <input
                type="password"
                value={settings.oauthClientSecret || ''}
                onChange={(e) => updateSetting('oauthClientSecret', e.target.value)}
                placeholder="Google OAuth2 Client Secret"
              />
            </div>

            <div className="form-group">
              <label>YouTube OAuth2-Verbindung</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: youtubeOAuthConnected ? '#4caf50' : '#9e9e9e',
                  fontWeight: 500
                }}>
                  <span style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: youtubeOAuthConnected ? '#4caf50' : '#9e9e9e',
                    display: 'inline-block'
                  }} />
                  {youtubeOAuthConnected ? 'Verbunden' : 'Nicht verbunden'}
                </span>
                {youtubeOAuthConnected ? (
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={disconnectYouTubeOAuth}
                    style={{ padding: '8px 16px' }}
                  >
                    Verbindung trennen
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={startYouTubeOAuth}
                    style={{ padding: '8px 16px' }}
                  >
                    Mit YouTube verbinden
                  </button>
                )}
              </div>
              <small style={{ display: 'block', marginTop: '6px' }}>
                OAuth2 ermöglicht das Senden von Chat-Nachrichten. Speichere zuerst Client ID und Secret.
              </small>
            </div>
          </>
        )}

        {platform === 'discord' && (
          <>
            <div className="form-group">
              <label>Bot Token</label>
              <input
                type="password"
                value={settings.token || ''}
                onChange={(e) => updateSetting('token', e.target.value)}
              />
              <small>Erstelle einen Bot im <a href="https://discord.com/developers/applications" target="_blank" rel="noopener noreferrer">Discord Developer Portal</a></small>
            </div>

            <div className="form-group">
              <label>Guild ID (Server ID)</label>
              <input
                type="text"
                value={settings.guildId || ''}
                onChange={(e) => updateSetting('guildId', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Command Prefix</label>
              <input
                type="text"
                value={settings.prefix || '!'}
                onChange={(e) => updateSetting('prefix', e.target.value)}
                maxLength="1"
              />
            </div>
          </>
        )}

        {platform === 'paypal' && (
          <>
            <div style={{background:'rgba(255,215,0,0.06)',border:'1px solid rgba(255,215,0,0.15)',borderRadius:10,padding:'14px 18px',marginBottom:24}}>
              <strong style={{color:'#FFD700',fontSize:13}}>📋 Setup-Anleitung</strong>
              <ol style={{color:'#aaa',fontSize:12,marginTop:8,paddingLeft:20,lineHeight:2}}>
                <li>Gehe zu <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noopener noreferrer" style={{color:'#FFD700'}}>developer.paypal.com</a> → My Apps &amp; Credentials</li>
                <li>Erstelle eine neue App (Live-Modus)</li>
                <li>Kopiere Client ID und Client Secret hierher</li>
                <li>Speichere und teste die Verbindung</li>
                <li>Teile die Donation-URL mit deinen Zuschauern</li>
              </ol>
            </div>

            <div className="form-group">
              <label>PayPal Client ID</label>
              <input type="text" value={settings.clientId || ''} onChange={e => updateSetting('clientId', e.target.value)} placeholder="AaBbCc..." />
              <small>Aus dem PayPal Developer Dashboard (Live-Modus)</small>
            </div>

            <div className="form-group">
              <label>PayPal Client Secret</label>
              <input type="password" value={settings.clientSecret || ''} onChange={e => updateSetting('clientSecret', e.target.value)}
                placeholder={settings._hasSecret ? '(gesetzt - neu eingeben zum Ändern)' : 'Client Secret eingeben'} />
              {settings._hasSecret && <small style={{color:'#00f593'}}>✓ Secret ist gesetzt. Nur neu eingeben wenn du es ändern möchtest.</small>}
            </div>

            <div className="form-group">
              <label>Modus</label>
              <select value={settings.sandbox ? 'sandbox' : 'live'} onChange={e => updateSetting('sandbox', e.target.value === 'sandbox')}>
                <option value="live">Live (echte Zahlungen)</option>
                <option value="sandbox">Sandbox (Test)</option>
              </select>
              <small>Sandbox für Tests, Live für echte Zahlungen</small>
            </div>

            <div className="form-group">
              <label>Währung</label>
              <select value={settings.currency || 'EUR'} onChange={e => updateSetting('currency', e.target.value)}>
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
                <option value="CHF">CHF</option>
              </select>
            </div>

            <div className="form-group">
              <label>Streamer Name (auf der Donation-Page)</label>
              <input type="text" value={settings.streamerName || ''} onChange={e => updateSetting('streamerName', e.target.value)} placeholder="Dein Streamer-Name" />
            </div>

            <div className="form-group">
              <label>Avatar URL (optional)</label>
              <input type="text" value={settings.streamerAvatar || ''} onChange={e => updateSetting('streamerAvatar', e.target.value)} placeholder="https://..." />
              <small>Dein Profilbild für die Donation-Page</small>
            </div>

            <div className="form-group">
              <label>Seiten-Titel</label>
              <input type="text" value={settings.pageTitle || ''} onChange={e => updateSetting('pageTitle', e.target.value)} placeholder="Unterstütze meinen Stream!" />
            </div>

            <div className="form-group">
              <label>Beschreibung</label>
              <input type="text" value={settings.pageDescription || ''} onChange={e => updateSetting('pageDescription', e.target.value)} placeholder="Danke für deine Unterstützung!" />
            </div>

            <div className="form-group">
              <label>Mindestbetrag ({settings.currency || 'EUR'})</label>
              <input type="number" min="0.5" step="0.5" value={settings.minAmount || 1} onChange={e => updateSetting('minAmount', parseFloat(e.target.value))} />
            </div>

            <div className="form-group">
              <label>Vorgeschlagene Beträge (kommagetrennt)</label>
              <input type="text"
                value={(settings.suggestedAmounts || [3,5,10,20]).join(', ')}
                onChange={e => updateSetting('suggestedAmounts', e.target.value.split(',').map(v => parseFloat(v.trim())).filter(v => !isNaN(v)))}
                placeholder="3, 5, 10, 20"
              />
            </div>

            <div className="form-group">
              <label>Akzentfarbe</label>
              <div style={{display:'flex',gap:10,alignItems:'center'}}>
                <input type="color" value={settings.accentColor || '#FFD700'} onChange={e => updateSetting('accentColor', e.target.value)} style={{width:44,height:38,borderRadius:6,border:'1px solid rgba(255,215,0,0.2)',cursor:'pointer',padding:3,background:'#0e0e10'}} />
                <input type="text" value={settings.accentColor || '#FFD700'} onChange={e => updateSetting('accentColor', e.target.value)} style={{flex:1}} />
              </div>
            </div>

            <div className="form-group">
              <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',textTransform:'none',letterSpacing:0,fontWeight:500,fontSize:14,color:'#ddd'}}>
                <input type="checkbox" checked={!!settings.alertOnDonation} onChange={e => updateSetting('alertOnDonation', e.target.checked)} style={{width:16,height:16,accentColor:'#FFD700'}} />
                Alert bei Donation auslösen
              </label>
              <small>Sendet einen Alert ans OBS-Overlay wenn jemand spendet</small>
            </div>

            <div className="form-group">
              <label>Verbindung testen</label>
              <button type="button" className="btn-primary" style={{marginTop:8}}
                onClick={async () => {
                  try {
                    await axios.post('/api/paypal/settings', settings);
                    const { data } = await axios.post('/api/paypal/test');
                    setPaypalTestResult({ ok: true, msg: data.message });
                  } catch (e) {
                    setPaypalTestResult({ ok: false, msg: e.response?.data?.error || 'Fehler' });
                  }
                }}>
                🔌 Verbindung testen
              </button>
              {paypalTestResult && (
                <div style={{marginTop:10,padding:'10px 14px',borderRadius:8,background:paypalTestResult.ok?'rgba(0,245,147,0.1)':'rgba(255,71,71,0.1)',border:`1px solid ${paypalTestResult.ok?'rgba(0,245,147,0.3)':'rgba(255,71,71,0.3)'}`,color:paypalTestResult.ok?'#00f593':'#ff6b6b',fontSize:13}}>
                  {paypalTestResult.ok ? '✓ ' : '✗ '}{paypalTestResult.msg}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Donation-URL für Zuschauer</label>
              <div style={{display:'flex',gap:8}}>
                <input type="text" value={window.location.origin.replace(':3000','') + ':6001/donate'} readOnly onClick={e => e.target.select()} />
                <button type="button" className="btn-primary" style={{whiteSpace:'nowrap'}}
                  onClick={() => { navigator.clipboard.writeText(window.location.origin.replace(':3000','') + ':6001/donate'); alert.success('URL kopiert!'); }}>
                  📋 Kopieren
                </button>
              </div>
              <small>Diese URL teilst du mit deinen Zuschauern — öffentlich zugänglich ohne Login</small>
            </div>
          </>
        )}

        {platform === 'streamelements' && (
          <>
            <div className="form-group">
              <label>JWT Token</label>
              <input
                type="password"
                value={settings.jwtToken || ''}
                onChange={(e) => updateSetting('jwtToken', e.target.value)}
                placeholder="StreamElements JWT Token"
              />
              <small>Dein JWT Token aus dem <a href="https://streamelements.com/dashboard/account/channels" target="_blank" rel="noopener noreferrer">StreamElements Dashboard</a></small>
            </div>

            <div className="form-group">
              <label>Channel ID</label>
              <input
                type="text"
                value={settings.channelId || ''}
                onChange={(e) => updateSetting('channelId', e.target.value)}
                placeholder="StreamElements Channel ID"
              />
            </div>

            <div className="form-group">
              <label>Daten importieren</label>
              <button
                type="button"
                className="btn-secondary"
                onClick={startImport}
                disabled={!!importProgress}
                style={{ marginTop: '8px' }}
              >
                {importProgress ? `Importiere... (${importProgress.processed}/${importProgress.total || '?'} ${importProgress.type})` : 'Daten importieren'}
              </button>
              {importStatus && (
                <small style={{ display: 'block', marginTop: '8px', color: '#9e9e9e' }}>
                  {importStatus}
                </small>
              )}
            </div>
          </>
        )}

        <div className="form-actions">
          <button type="submit" className="btn-success">
            Einstellungen speichern
          </button>
        </div>
      </form>
    </div>
  );
}

export default Settings;
